import React, { useMemo } from "react";
import {
  isGoalserveFootballLogo,
  resolveFootballDefaultShieldUrl,
  resolveFootballTeamLogoS3UrlsFromName,
} from "../../../utils/footballTeamLogoResolve";
import { useResolvedImageSrc } from "../../../utils/useResolvedImageSrc";

/**
 * Football team logo — S3 crest(s) with youth/women/reserve suffix fallbacks, then Goalserve
 * base64, then default shield on S3 (same fallbackOveride rules as CricketTeamLogo).
 *
 * Prefers a session-cached URL (or the first candidate optimistically), then confirms
 * off-DOM so missing assets never flash the browser broken-image icon or the shield.
 */
export const FootballTeamLogo = ({
  teamName = "",
  height = "40px",
  width = "40px",
  logo = null,
  fallbackOveride = "false",
  className = "",
}) => {
  const fallbackShieldUrl = useMemo(
    () => resolveFootballDefaultShieldUrl(fallbackOveride),
    [fallbackOveride]
  );
  const safeTeamName = (teamName || "").toString().trim();
  const s3Urls = useMemo(
    () => resolveFootballTeamLogoS3UrlsFromName(safeTeamName),
    [safeTeamName]
  );
  const goalserveLogo = isGoalserveFootballLogo(logo) ? logo : null;
  const candidateUrls = useMemo(() => {
    const urls = [...s3Urls];
    if (goalserveLogo && !urls.includes(goalserveLogo)) {
      urls.push(goalserveLogo);
    }
    return urls;
  }, [s3Urls, goalserveLogo]);

  const { src, hidden, onError } = useResolvedImageSrc(candidateUrls, fallbackShieldUrl);
  const isBase64 = Boolean(goalserveLogo && src === goalserveLogo);

  function handleLoad(event) {
    const img = event.target;
    img.style.borderRadius = "0";
  }

  return (
    <div
      className={`flex shrink-0 items-center justify-center overflow-hidden rounded-none ${className}`.trim()}
      style={{ width: width, height: height, minWidth: width, minHeight: height }}
    >
      <img
        src={src}
        onError={onError}
        alt={teamName ? `${teamName} team logo` : "Team logo"}
        onLoad={handleLoad}
        className="block h-full w-full max-h-full max-w-full rounded-none object-contain"
        style={{
          width,
          height,
          maxWidth: width,
          maxHeight: height,
          borderRadius: 0,
          ...(hidden ? { visibility: "hidden" } : {}),
        }}
        loading={isBase64 ? "eager" : undefined}
        decoding="async"
      />
    </div>
  );
};

export default FootballTeamLogo;
